/**
 * I cataloghi che si caricano in blocco, e cosa cambia da uno all'altro.
 *
 * Il modo di lavorare e' sempre quello: si prende un archivio ZIP di
 * documenti, si fa analizzare all'app - che restituisce il modello Excel
 * gia' compilato con quel che e' riuscita a capire - si completano a mano le
 * righe in giallo e si ricarica il tutto. Cambiano solo gli indirizzi, le
 * etichette e il nome di chi possiede il documento: il dipendente, il mezzo,
 * e domani il cantiere.
 *
 * Per aggiungere un catalogo si scrive un profilo qui sotto, senza toccare
 * la finestra: e' il motivo per cui questi dati stanno in un file a parte.
 */

export const PROFILO_DIPENDENTI = {
  codice: "dipendenti",
  titolo: "Import massivo documenti dipendenti",
  modelloUrl: "/employees/import-template",
  modelloFile: "modello_import_documenti_SGM.xlsx",
  analisiUrl: "/employees/documents/analyze",
  analisiFile: "analisi_documenti_SGM.xlsx",
  importUrl: "/employees/bulk-import-documents",
  campoTipo: "default_doc_class",
  spiegaModello:
    "Il modello indica per ogni file a quale dipendente appartiene e quali dati "
    + "inserire (tipo di corso, tipologia, ore, ente formatore, date).",
  notaModello:
    "Il modello contiene già l'elenco del personale con le matricole: la colonna "
    + "«dipendente» si compila scegliendo dall'elenco a discesa.",
  spiegaZip:
    "I file possono stare tutti insieme oppure in cartelle nominate col badge o "
    + "col nominativo (Cognome Nome): in quel caso l'abbinamento riesce anche "
    + "senza Excel.",
  esempioZip: [
    "documenti.zip/",
    "  SGM0001/antincendio.pdf",
    "  Rossi Marco/carta_identita.pdf",
    "  primo_soccorso_bianchi.pdf",
  ],
  tipoEtichetta: "Sezione predefinita",
  tipoIniziale: "attestato",
  tipoOpzioni: [
    { valore: "attestato", nome: "Attestati" },
    { valore: "personale", nome: "Documenti personali" },
  ],
  tipoNota: "Usata solo per i file senza colonna «classe» nell'Excel.",
  archiviaEtichetta:
    "Archivia i documenti precedenti dello stesso tipo per lo stesso dipendente "
    + "(utile per i rinnovi)",
  contatoreSoggetti: { chiave: "employees_matched", nome: "Dipendenti" },
  avvisoScarti:
    "Alcuni file non sono stati importati. Il motivo è indicato nell'ultima "
    + "colonna: di solito il badge o il nominativo non corrisponde a nessun "
    + "dipendente in anagrafica.",
  colonneReport: [
    { nome: "Dipendente", valore: (d) => d.employee },
    { nome: "Tipo", valore: (d) => d.category },
  ],
  campiCsv: ["employee", "badge", "doc_class", "category"],
};

export const PROFILO_MEZZI = {
  codice: "mezzi",
  titolo: "Import massivo documenti di mezzi e attrezzature",
  modelloUrl: "/equipment/import-template",
  modelloFile: "modello_import_attrezzature_SGM.xlsx",
  analisiUrl: "/equipment/documents/analyze",
  analisiFile: "analisi_documenti_mezzi_SGM.xlsx",
  importUrl: "/equipment/bulk-import-documents-v2",
  campoTipo: "default_doc_type",
  spiegaModello:
    "Il modello indica per ogni file a quale mezzo appartiene (targa o "
    + "matricola), che documento è, il titolo e le date.",
  notaModello:
    "Il foglio «Mezzi» del modello porta tutti gli identificativi: l'identificativo "
    + "va copiato di lì, identico.",
  spiegaZip:
    "I file possono stare tutti insieme oppure in cartelle nominate con la targa "
    + "o la matricola: in quel caso l'abbinamento riesce anche senza Excel. "
    + "Due file che si chiamano uguale in cartelle diverse non si confondono.",
  esempioZip: [
    "archivio.zip/",
    "  FS845YG/libretto.pdf",
    "  CZ996PF/dichiarazione CE.pdf",
    "  tagliando_30000km_FS845YG.pdf",
  ],
  tipoEtichetta: "Tipo documento predefinito",
  tipoIniziale: "ce",
  tipoOpzioni: [
    { valore: "ce", nome: "Documentazione" },
    { valore: "maintenance", nome: "Documento manutenzione" },
    { valore: "logbook", nome: "Libro manutenzione" },
    { valore: "taratura", nome: "Taratura" },
    { valore: "libretto", nome: "Libretto di circolazione" },
    { valore: "revisione", nome: "Revisione" },
    { valore: "altro", nome: "Altro" },
  ],
  tipoNota: "Usato solo per i file senza colonna «tipo_documento» nell'Excel.",
  archiviaEtichetta:
    "Archivia la documentazione precedente dello stesso mezzo (utile per i rinnovi)",
  contatoreSoggetti: { chiave: "equipment_matched", nome: "Mezzi" },
  avvisoScarti:
    "Alcuni file non sono stati importati — di solito perché l'identificativo "
    + "indicato non corrisponde a nessun mezzo in anagrafica attrezzature.",
  colonneReport: [
    { nome: "Mezzo", valore: (d) => d.equipment },
    { nome: "Tipo", valore: (d) => d.doc_type },
  ],
  campiCsv: ["equipment", "doc_type"],
};

export const PROFILO_CANTIERI = {
  codice: "cantieri",
  titolo: "Import massivo documenti di cantiere",
  modelloUrl: "/sites/documents/import-template",
  modelloFile: "modello_import_documenti_cantiere_SGM.xlsx",
  analisiUrl: "/sites/documents/analyze",
  analisiFile: "analisi_documenti_cantiere_SGM.xlsx",
  importUrl: "/sites/bulk-import-documents",
  campoTipo: "default_doc_type",
  spiegaModello:
    "Il modello indica per ogni file a quale cantiere appartiene, che documento "
    + "è, le date e se può vederlo chi inquadra il QR del cantiere.",
  notaModello:
    "Il foglio «Cantieri» del modello porta tutti i nomi con indirizzo, "
    + "committente e commessa: il nome va copiato di lì, identico.",
  spiegaZip:
    "I file possono stare tutti insieme oppure in cartelle nominate col "
    + "cantiere o col numero di commessa. Se più di un cantiere combacia — "
    + "una cartella che dice solo «Pavia» — l'app non sceglie e te lo scrive.",
  esempioZip: [
    "cantieri.zip/",
    "  Pavia - Via Teodoro Lovati 15/notifica.pdf",
    "  Pavia Lovati/ordinanza occupazione suolo.pdf",
    "  POS_melegnano_marconi.pdf",
  ],
  tipoEtichetta: "Tipo documento predefinito",
  tipoIniziale: "Altro",
  tipoOpzioni: [
    { valore: "POS", nome: "POS" },
    { valore: "Notifica preliminare", nome: "Notifica preliminare" },
    { valore: "Ordinanza", nome: "Ordinanza" },
    { valore: "Doc dal Committente", nome: "Doc dal Committente" },
    { valore: "Fir e Analisi", nome: "Fir e Analisi" },
    { valore: "Segnaletica", nome: "Segnaletica" },
    { valore: "Foto dei luoghi", nome: "Foto dei luoghi" },
    { valore: "Altro", nome: "Altro" },
  ],
  tipoNota: "Usato solo per i file senza colonna «tipo_documento» nell'Excel.",
  archiviaEtichetta:
    "Archivia i documenti precedenti dello stesso tipo sullo stesso cantiere",
  contatoreSoggetti: { chiave: "sites_matched", nome: "Cantieri" },
  avvisoScarti:
    "Alcuni file non sono stati importati — di solito perché il cantiere non è "
    + "stato individuato, o perché ne combaciava più d'uno e l'app non ha "
    + "scelto al posto tuo.",
  colonneReport: [
    { nome: "Cantiere", valore: (d) => d.site },
    { nome: "Tipo", valore: (d) => d.doc_type },
  ],
  campiCsv: ["site", "doc_type"],
};
